/**
 * Leap Checkout Helper — Stripe Integration
 *
 * Shared checkout function for all Leap static pages.
 * Calls the create-checkout-session Edge Function, then redirects to Stripe Checkout.
 *
 * Usage:
 *   <script src="/js/checkout.js"></script>
 *   <button onclick="startCheckout({ priceId: 'price_xxx', productKey: 'playbook', city: 'barcelona' })">
 *     Get Playbook — $149
 *   </button>
 */ // Edge Function endpoint
const CHECKOUT_API = 'https://unpidapxllhieiwefiem.supabase.co/functions/v1/create-checkout-session';
/**
 * Start a Stripe Checkout session and redirect.
 *
 * @param {Object} options
 * @param {string} options.priceId       - Stripe Price ID (e.g. "price_xxx")
 * @param {string} options.productKey    - Product identifier for webhook routing
 *                                         (e.g. "playbook", "bundle", "launch_pad_pro",
 *                                          "launch_pad_bundle", "accelerator", "membership",
 *                                          "custom_city_report")
 * @param {string} [options.city]        - Destination city slug (default: auto-detected from URL)
 * @param {string} [options.email]       - Pre-fill customer email
 * @param {string} [options.successUrl]  - Redirect URL after successful payment
 * @param {string} [options.cancelUrl]   - Redirect URL if customer cancels
 * @param {HTMLElement} [options.buttonEl] - Button element to disable during request
 */ async function startCheckout(options) {
    const { priceId, productKey, city, email, successUrl, cancelUrl, buttonEl } = options;
    // Auto-detect city from URL path if not provided
    const detectedCity = city || detectCityFromUrl();
    // Capture UTM parameters from URL or localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get('utm_source') || localStorage.getItem('leap_utm_source') || null;
    const utmMedium = urlParams.get('utm_medium') || localStorage.getItem('leap_utm_medium') || null;
    const utmCampaign = urlParams.get('utm_campaign') || localStorage.getItem('leap_utm_campaign') || null;
    // Disable button to prevent double-clicks
    let originalText = '';
    if (buttonEl) {
        originalText = buttonEl.textContent;
        buttonEl.disabled = true;
        buttonEl.textContent = 'Redirecting...';
    }
    try {
        const body = {
            priceId,
            productKey,
            city: detectedCity
        };
        if (email) body.customerEmail = email;
        if (successUrl) body.successUrl = successUrl;
        if (cancelUrl) body.cancelUrl = cancelUrl;
        if (utmSource) body.utmSource = utmSource;
        if (utmMedium) body.utmMedium = utmMedium;
        if (utmCampaign) body.utmCampaign = utmCampaign;
        // Attach blueprint quiz data from localStorage (if available)
        // This bridges the free blueprint answers into the Stripe metadata
        // so the webhook can populate the user's profile server-side.
        try {
            const quizKey = 'leap_quiz_' + detectedCity;
            const quizRaw = localStorage.getItem(quizKey);
            if (quizRaw) {
                const quiz = JSON.parse(quizRaw);
                body.quizData = {
                    timeline: quiz.timeline || null,
                    employment: quiz.employment || null,
                    household: quiz.household || null,
                    housing: Array.isArray(quiz.housing) ? quiz.housing.join(',') : null,
                    concerns: Array.isArray(quiz.concerns) ? quiz.concerns.join(',') : null
                };
            }
        } catch (_e) {}
        const res = await fetch(CHECKOUT_API, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
        });
        if (!res.ok) {
            const err = await res.json().catch(()=>({
                    error: 'Unknown error'
                }));
            throw new Error(err.error || 'Failed to create checkout session');
        }
        const { url } = await res.json();
        if (!url) throw new Error('No checkout URL returned');
        // Redirect to Stripe Checkout
        window.location.href = url;
    } catch (err) {
        console.error('Checkout error:', err);
        // Restore button
        if (buttonEl) {
            buttonEl.disabled = false;
            buttonEl.textContent = originalText;
        }
        // Show error to user
        if (typeof showToast === 'function') showToast('Something went wrong. Please try again.', 'error');
        else alert('Something went wrong starting checkout. Please try again.');
    }
}
/**
 * Detect city slug from current page URL.
 * Matches /barcelona/, /paris/, etc. in the path.
 */ function detectCityFromUrl() {
    const cities = [
        'barcelona',
        'paris',
        'lisbon',
        'berlin',
        'athens',
        'valencia',
        'dublin',
        'amsterdam',
        'london'
    ];
    const path = window.location.pathname.toLowerCase();
    for (const city of cities){
        if (path.includes('/' + city + '/') || path.includes('/' + city + '.')) return city;
    }
    return null; // No city detected — let checkout handle gracefully
}
/**
 * Open Stripe Customer Portal for billing management.
 * Redirects to the portal URL (set up in Stripe Dashboard).
 */ function openBillingPortal() {
    window.location.href = 'https://billing.stripe.com/p/login/aFadR85i7aUNgQk6lqbjW00';
}
// Persist UTM params in localStorage so they survive navigation
(function persistUtmParams() {
    try {
        const params = new URLSearchParams(window.location.search);
        [
            'utm_source',
            'utm_medium',
            'utm_campaign'
        ].forEach(function(key) {
            const val = params.get(key);
            if (val) localStorage.setItem('leap_' + key, val);
        });
    } catch (_e) {}
})();

//# sourceMappingURL=plan.844a19e4.js.map
